<template>
  <mobile-responsive-shell :title="title">
    <div v-if="purchase && purchase.reference" class="pur-detail">
      <div class="pur-detail-actions">
        <template v-if="userRole === 'Admin'">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="refresh">
            <i class="bi bi-check2-square"></i> Valider
          </button>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="goEdit">
            <i class="bi bi-pencil-square"></i> Modifier
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="remove(purchase.reference)">
            <i class="bi bi-trash"></i>
          </button>
        </template>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="printPage">
          <i class="bi bi-printer"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="refresh">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      <p class="pur-detail-ref text-muted small mb-2">
        Facture réf. <strong>{{ purchase.reference }}</strong>
      </p>

      <section class="pur-detail-card">
        <h3 class="pur-detail-h">Fournisseur</h3>
        <dl class="pur-kv">
          <div><dt>Société</dt><dd>{{ purchase.supplier ? purchase.supplier.society : "—" }}</dd></div>
          <div><dt>Téléphone</dt><dd>{{ purchase.supplier ? purchase.supplier.phone : "—" }}</dd></div>
        </dl>
      </section>

      <section class="pur-detail-card">
        <h3 class="pur-detail-h">Montants</h3>
        <dl class="pur-kv">
          <div><dt>Qté totale</dt><dd>{{ purchase.totalQuantity != null ? purchase.totalQuantity : "—" }}</dd></div>
          <template v-if="userRole === 'Admin'">
            <div><dt>M. TTC</dt><dd class="text-primary">{{ purchase.totalPriceTTC }}</dd></div>
            <div><dt>M. HT</dt><dd>{{ purchase.totalPriceHT }}</dd></div>
            <div><dt>M. TVA</dt><dd>{{ purchase.totalPriceTVA }}</dd></div>
            <div><dt>Payé</dt><dd class="text-success">{{ purchase.amountPaid }}</dd></div>
            <div><dt>Impayé</dt><dd class="text-danger">{{ purchase.amountUnpaid }}</dd></div>
          </template>
        </dl>
      </section>

      <section class="pur-detail-card">
        <h3 class="pur-detail-h">Livraison / date</h3>
        <dl class="pur-kv">
          <div><dt>Date</dt><dd>{{ purchase.date || "—" }}</dd></div>
        </dl>
      </section>

      <section class="pur-detail-card">
        <h3 class="pur-detail-h">Lignes</h3>
        <p v-if="!lineItems.length" class="text-muted mb-0">Aucune ligne.</p>
        <article
          v-for="(item, idx) in lineItems"
          :key="item.id || item.item_reference || idx"
          class="pur-line-card"
        >
          <div class="pur-line-title">
            <span v-if="item.item">{{ item.item.fullName }}</span>
            <span v-else>{{ item.item_reference }}</span>
          </div>
          <dl class="pur-kv pur-kv--compact">
            <div><dt>Réf.</dt><dd>{{ item.item_reference }}</dd></div>
            <div><dt>Qté</dt><dd>{{ item.quantity }}</dd></div>
            <div v-if="userRole === 'Admin'"><dt>P.U. TTC</dt><dd>{{ item.priceTTC }}</dd></div>
            <div v-if="userRole === 'Admin'"><dt>Total TTC</dt><dd>{{ item.totalPriceTTC }}</dd></div>
            <div v-if="item.stock">
              <dt>Stock</dt><dd>{{ item.stock.fullName }} / {{ item.stock.reference }}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section v-if="userRole === 'Admin' && paymentRows.length" class="pur-detail-card">
        <h3 class="pur-detail-h">Paiements</h3>
        <article
          v-for="(payment, idx) in paymentRows"
          :key="payment.reference || idx"
          class="pur-pay-card"
        >
          <div class="d-flex justify-content-between align-items-start gap-2">
            <div>
              <div class="fw-semibold">{{ payment.reference }}</div>
              <div class="text-muted small">{{ payment.date }} · {{ payment.paymentMethod }}</div>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="goPayment(payment.reference)"
            >
              <i class="bi bi-info-square"></i>
            </button>
          </div>
          <dl class="pur-kv pur-kv--compact mt-2 mb-0">
            <div><dt>Payé</dt><dd class="text-success">{{ payment.amount }}</dd></div>
            <div><dt>À régler</dt><dd class="text-danger">{{ payment.remainingAmount }}</dd></div>
            <div v-if="payment.user"><dt>Par</dt><dd>{{ payment.user.name }}</dd></div>
            <div v-if="payment.remark"><dt>Remarque</dt><dd>{{ payment.remark }}</dd></div>
          </dl>
        </article>
      </section>
    </div>
    <p v-else class="text-muted text-center py-4">Chargement…</p>
  </mobile-responsive-shell>
</template>

<script>
import { mapGetters } from "vuex";
import MobileResponsiveShell from "../components/MobileResponsiveShell.vue";

export default {
  name: "MobilePurchasesDetails",
  components: { MobileResponsiveShell },
  computed: {
    ...mapGetters("purchase", { purchase: "getPurchase" }),
    ...mapGetters("auth", { userRole: "getUserRole" }),
    title() {
      const ref = this.$route.params.reference;
      return ref ? `Achat ${ref}` : "Détail achat";
    },
    lineItems() {
      const items = this.purchase && this.purchase.purchase_items;
      return Array.isArray(items) ? items : [];
    },
    paymentRows() {
      const p = this.purchase && this.purchase.payments;
      return Array.isArray(p) ? p : [];
    },
  },
  mounted() {
    this.$store.dispatch("purchase/show", this.$route.params.reference);
  },
  methods: {
    async refresh() {
      await this.$store.dispatch("purchase/show", this.$route.params.reference);
    },
    goEdit() {
      this.$router.push({
        name: "mobile-purchases-edit",
        params: { reference: this.purchase.reference },
      });
    },
    goPayment(reference) {
      this.$router.push({
        name: "mobile-payments-suppliers-details",
        params: { reference },
      });
    },
    printPage() {
      window.print();
    },
    async remove(reference) {
      await this.$store.dispatch("purchase/destroy", reference);
    },
  },
};
</script>

<style scoped>
.pur-detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.pur-detail-card {
  background: #fff;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.pur-detail-h {
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: #495057;
}
.pur-kv {
  margin: 0;
  display: grid;
  gap: 0.35rem;
}
.pur-kv > div {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.9rem;
}
.pur-kv dt {
  margin: 0;
  color: #6c757d;
  font-weight: 500;
}
.pur-kv dd {
  margin: 0;
  text-align: right;
  word-break: break-word;
}
.pur-kv--compact > div {
  font-size: 0.85rem;
}
.pur-line-card,
.pur-pay-card {
  padding: 0.65rem 0;
  border-bottom: 1px solid #eee;
}
.pur-line-card:last-child,
.pur-pay-card:last-child {
  border-bottom: none;
}
.pur-line-title {
  font-weight: 600;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
}
</style>
